import has from 'lodash/has';
import omit from 'lodash/omit';
import React from 'react';
import './BaseInput.css';

/*
  _   _                 ____         ____                                  
 | | | | ___ _ __ ___  | __ )  ___  |  _ \ _ __ __ _  __ _  ___  _ __  ___ 
 | |_| |/ _ \ '__/ _ \ |  _ \ / _ \ | | | | '__/ _` |/ _` |/ _ \| '_ \/ __|
 |  _  |  __/ | |  __/ | |_) |  __/ | |_| | | | (_| | (_| | (_) | | | \__ \
 |_| |_|\___|_|  \___| |____/ \___| |____/|_|  \__,_|\__, |\___/|_| |_|___/
                                                     |___/                 

NOTE: Ths strange logic involving storing the value of the input in state
      if the input is controlled is a workaround for an issue in React
      where the cursor position is lost after clicking somewhere in the
      input and typing.

      https://github.com/facebook/react/issues/955#issuecomment-469352730
*/

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  // Defeats the value from coming from state. Helpful for certain configs
  // where the input always needs to be "controlled".
  id: string;
  valueAlwaysFromProps: boolean;
  inputType: string;
}

interface State {
  value: string | number | readonly string[] | undefined;
  fromOnChange: boolean;
}

class BaseInput extends React.Component<Props, State> {
  static defaultProps = {
    style: {},
    disabled: false,
    valueAlwaysFromProps: false,
    inputType: 'base',
  };

  // This gets called on state change, not just prop changes.
  // Must not use prop value when coming from internal onChange state update.
  static getDerivedStateFromProps(props: Props, state: State): State | null {
    if (has(props, 'value')) {
      // The ordering here looks a little goofy, but it's important.
      if (props.valueAlwaysFromProps) {
        return { value: props.value, fromOnChange: false };
      }

      if (state.fromOnChange) {
        return { value: state.value, fromOnChange: false };
      }

      if (props.value !== state.value) {
        return { value: props.value, fromOnChange: false };
      }
    }

    return null;
  }

  constructor(props: Props) {
    super(props);

    this.state = {
      value: props.value,
      fromOnChange: false,
    };
  }

  handleChange = (evt: React.ChangeEvent<HTMLInputElement>): void => {
    // If it doesn't have 'value' prop, then don't call setState.
    // This is an issue with uncontrolled inputs.
    if (has(this.props, 'value')) {
      this.setState({ value: evt.target.value, fromOnChange: true });
    }

    if (this.props.onChange) {
      this.props.onChange(evt);
    }
  };

  render(): React.ReactNode {
    const { style, disabled, inputType, ...restProps } = this.props;

    const inputStyles: React.CSSProperties = {
      whiteSpace: 'nowrap',
      wordBreak: 'keep-all',
      ...style,
    };

    const restPropsWithoutValues = omit(restProps, [
      'value',
      'valueAlwaysFromProps',
    ]);

    const finalProps = {
      ...restPropsWithoutValues,
      ...(has(restProps, 'value') ? { value: this.state.value } : {}),
    };

    return (
      <input
        {...finalProps}
        // eslint-disable-next-line react/no-unknown-property
        input-type={inputType}
        style={inputStyles}
        disabled={disabled}
        onChange={this.handleChange}
      />
    );
  }
}

export default BaseInput;
